import { useEffect, useRef, useState } from 'react';

import { creditRequest } from './credit-api';

export const useCreditRequest = (cloudId: string) => {
	const [status, setStatus] = useState<'ready' | 'sending' | 'sent' | 'unavailable'>('ready');
	const [error, setError] = useState<'failed' | 'rateLimited' | null>(null);
	const [retryAt, setRetryAt] = useState(0);
	const submission = useRef<AbortController | null>(null);
	useEffect(
		() => () => {
			submission.current?.abort();
			submission.current = null;
		},
		[],
	);
	useEffect(() => {
		if (!retryAt) return undefined;
		const timeout = setTimeout(() => setRetryAt(0), Math.max(0, retryAt - Date.now()));
		return () => clearTimeout(timeout);
	}, [retryAt]);
	const send = async () => {
		if (submission.current || retryAt || status === 'sent' || status === 'unavailable') return;
		const controller = new AbortController();
		submission.current = controller;
		setStatus('sending');
		setError(null);
		const timeout = setTimeout(() => controller.abort(), 10000);
		try {
			const response = await creditRequest(cloudId, 'requests', 'POST', controller.signal);
			if (submission.current !== controller) return;
			if (response.status === 202) setStatus('sent');
			else if (response.status === 429) {
				const seconds = Number(response.headers.get('Retry-After'));
				const delay = Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 60000;
				setRetryAt(Date.now() + Math.min(delay, 2147483647));
				setError('rateLimited');
				setStatus('ready');
			} else if (response.status >= 500) {
				setError('failed');
				setStatus('ready');
			} else setStatus('unavailable');
		} catch {
			// Explicit retries are safe: the server deduplicates an uncertain submission.
			if (submission.current === controller) {
				setError('failed');
				setStatus('ready');
			}
		} finally {
			clearTimeout(timeout);
			if (submission.current === controller) submission.current = null;
		}
	};
	return { status, error, rateLimited: retryAt !== 0, send };
};
